import React, { useState } from "react";
import { siteData } from "../mock";
import { Mail, MapPin, Phone, Send } from "lucide-react";
import { useToast } from "../hooks/use-toast";
import TrustBadges from "./TrustBadges";
import { Link } from "react-router-dom";

export default function ContactSection() {
  const { contact } = siteData;
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast({
        title: "Missing info",
        description: "Please enter your name and email.",
      });
      return;
    }
    // Store locally as backup
    const existing = JSON.parse(localStorage.getItem("td_leads") || "[]");
    existing.push({ ...form, at: new Date().toISOString() });
    localStorage.setItem("td_leads", JSON.stringify(existing));

    // Fire off email notification to Chris via Formsubmit
    try {
      const cfg = siteData.notify;
      if (cfg?.endpoint) {
        await fetch(cfg.endpoint, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            _subject: `New contact inquiry — ${form.name}`,
            _template: "table",
            _captcha: "false",
            client_name: form.name,
            client_email: form.email,
            client_phone: form.phone || "—",
            message: form.message || "—",
            submitted_at: new Date().toLocaleString(),
          }),
        });
      }
    } catch (err) {
      console.warn("Contact notification failed:", err);
    }

    toast({
      title: "Thanks for reaching out!",
      description: "Chris will get back to you shortly.",
    });
    setForm({ name: "", email: "", phone: "", message: "" });
  };

  return (
    <section id="contact" className="relative bg-purple-band py-24">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="mb-2">
          <span className="deal-yellow font-heading font-semibold tracking-[0.3em] text-sm md:text-base">
            GET IN TOUCH
          </span>
        </div>
        <h2 className="font-display text-white font-bold text-5xl md:text-7xl tracking-wide mb-4">
          SCHEDULE NOW
        </h2>
        <p className="text-white/70 text-lg mb-14">
          Reach out directly or book your preferred session/package above.
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          {/* Contact Info */}
          <div className="space-y-6">
            <div className="card-surface rounded-xl p-6 flex items-center gap-4">
              <div className="w-12 h-12 rounded-md bg-[#e10600] flex items-center justify-center">
                <Mail size={22} className="text-[#0a0a0a]" />
              </div>
              <div>
                <p className="text-white/60 text-xs tracking-[0.25em] font-heading mb-1">EMAIL</p>
                <a href={`mailto:${contact.email}`} className="text-white hover:deal-yellow text-lg font-medium">
                  {contact.email}
                </a>
              </div>
            </div>
            <div className="card-surface rounded-xl p-6 flex items-center gap-4">
              <div className="w-12 h-12 rounded-md bg-[#e10600] flex items-center justify-center">
                <Phone size={22} className="text-[#0a0a0a]" />
              </div>
              <div>
                <p className="text-white/60 text-xs tracking-[0.25em] font-heading mb-1">PHONE</p>
                <a href={`tel:${contact.phone}`} className="text-white text-lg font-medium">
                  {contact.phone}
                </a>
              </div>
            </div>
            <div className="card-surface rounded-xl p-6 flex items-start gap-4">
              <div className="w-12 h-12 rounded-md bg-[#e10600] flex items-center justify-center shrink-0">
                <MapPin size={22} className="text-white" />
              </div>
              <div className="min-w-0">
                <p className="text-white/60 text-xs tracking-[0.25em] font-heading mb-1">LOCATION</p>
                <a
                  href="https://www.google.com/maps/search/?api=1&query=10521+Croatia+Way+Manassas+VA+20109"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white text-lg font-medium hover:text-[#e10600] transition-colors block leading-snug"
                >
                  {contact.address}
                </a>
                <p className="text-white/55 text-sm mt-1">By appointment only</p>
              </div>
            </div>

            {/* Map iframe */}
            <div className="rounded-xl overflow-hidden border border-[#e10600]/20 h-56">
              <iframe
                title="THEDEAL FITNESS Location"
                src="https://www.google.com/maps?q=10521+Croatia+Way+Manassas+VA+20109&output=embed"
                className="w-full h-full grayscale contrast-125"
                loading="lazy"
              />
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="card-surface rounded-xl p-8 space-y-5">
            <div>
              <label className="block text-white/70 text-xs tracking-[0.25em] font-heading mb-2">
                YOUR NAME *
              </label>
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full bg-[#141414] border border-white/10 rounded-md px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:border-[#e10600] transition-colors"
                placeholder="Full name"
              />
            </div>
            <div>
              <label className="block text-white/70 text-xs tracking-[0.25em] font-heading mb-2">
                EMAIL *
              </label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full bg-[#141414] border border-white/10 rounded-md px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:border-[#e10600] transition-colors"
                placeholder="you@example.com"
              />
            </div>
            <div>
              <label className="block text-white/70 text-xs tracking-[0.25em] font-heading mb-2">
                PHONE
              </label>
              <input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="w-full bg-[#141414] border border-white/10 rounded-md px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:border-[#e10600] transition-colors"
                placeholder="(optional)"
              />
            </div>
            <div>
              <label className="block text-white/70 text-xs tracking-[0.25em] font-heading mb-2">
                MESSAGE
              </label>
              <textarea
                rows={4}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="w-full bg-[#141414] border border-white/10 rounded-md px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:border-[#e10600] transition-colors resize-none"
                placeholder="Tell me about your goals..."
              />
            </div>
            <button
              type="submit"
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-md bg-[#e10600] text-[#0a0a0a] font-heading font-bold tracking-[0.2em] text-sm hover:bg-[#ff1a1a] transition-colors"
            >
              SEND MESSAGE
              <Send size={16} />
            </button>

            {/* Trust + privacy note */}
            <TrustBadges variant="light" className="mt-2" />
            <p className="text-white/55 text-xs leading-relaxed pt-1">
              Your info stays private and is only used to reply to your inquiry. Read our{" "}
              <Link to="/privacy" className="text-[#e10600] hover:underline font-semibold">
                Privacy Policy
              </Link>
              .
            </p>
          </form>
        </div>
      </div>
    </section>
  );
}
